import { useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const TESTIMONIALS = [
  {
    name: 'Sandra & Michael Torres',
    location: 'Las Cruces, NM',
    quote: 'Brainard\'s completely transformed our backyard into something we never imagined possible. The stone patio and modern landscaping are absolutely stunning. Every single neighbor comments on it.',
    outcome: 'Full Backyard Transformation',
    rating: 5,
    featured: true,
    img: 'https://brainardsgreenscapes.com/assets/images/after.jpeg',
  },
  {
    name: 'James Whitfield',
    location: 'El Paso, TX',
    quote: 'Professional, on time, and incredibly skilled. My hardscape upgrade looks better than I ever imagined — clean lines, durable materials, and beautifully finished.',
    outcome: 'Metro Verde Hardscape Upgrade',
    rating: 5,
    featured: false,
  },
  {
    name: 'Rebecca Alarcon',
    location: 'Picacho Hills, NM',
    quote: 'A once dry yard turned into a vibrant, lush outdoor space. Brainard\'s used their artificial grass solutions to create something truly amazing. Worth every penny.',
    outcome: 'Artificial Grass Installation',
    rating: 5,
    featured: false,
  },
  {
    name: 'David Chen',
    location: 'Sonoma, Las Cruces',
    quote: 'The complete makeover featuring seamless artificial lawn and modern stone work exceeded all my expectations. Brainard\'s commitment to quality is unmatched.',
    outcome: 'Sonoma Lawn Complete Makeover',
    rating: 5,
    featured: false,
  },
];

export default function Testimonials() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });

  const featured = TESTIMONIALS[0];
  const rest = TESTIMONIALS.slice(1);

  return (
    <section className="bg-ivory-100 py-28">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="font-sans text-forest-500 text-xs tracking-[0.25em] uppercase mb-4">What Clients Say</p>
          <h2 className="font-serif text-charcoal-900 font-semibold leading-tight mb-4" style={{ fontSize: 'clamp(32px, 4vw, 52px)' }}>
            Real Results, Real Clients
          </h2>
          <div className="flex items-center justify-center gap-1.5 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-fill text-gold-400 text-lg" />
              </div>
            ))}
          </div>
          <p className="font-sans text-charcoal-700 text-sm">
            4.9 stars across <strong>35+ verified five-star reviews</strong>
          </p>
        </div>

        {/* Featured testimonial with image */}
        <FeaturedTestimonial testimonial={featured} />

        {/* 3-col grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {rest.map((t, i) => (
            <SmallTestimonialCard key={t.name} testimonial={t} delay={i * 100} />
          ))}
          {/* CTA Card */}
          <TestimonialCTA />
        </div>
      </div>
    </section>
  );
}

function FeaturedTestimonial({ testimonial }: { testimonial: typeof TESTIMONIALS[0] }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`relative rounded-lg overflow-hidden h-72 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <img
        src={testimonial.img}
        alt={`${testimonial.outcome} by Brainard's Greenscapes`}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/90 via-charcoal-900/60 to-charcoal-900/20" />

      <div className="relative z-10 h-full flex items-center p-12">
        <div className="max-w-xl">
          <div className="flex gap-1 mb-5">
            {Array.from({ length: testimonial.rating }).map((_, i) => (
              <div key={i} className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-gold-400 text-sm" />
              </div>
            ))}
          </div>
          {/* Large decorative quote mark */}
          <div className="font-serif text-stone-400/30 text-8xl leading-none mb-2 -ml-2">"</div>
          <blockquote className="font-serif text-white text-xl italic leading-relaxed mb-6 -mt-8">
            {testimonial.quote}
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-10 h-0.5 bg-stone-400" />
            <div>
              <div className="font-sans text-white text-sm font-semibold">{testimonial.name}</div>
              <div className="font-sans text-stone-400 text-xs">{testimonial.location} &bull; {testimonial.outcome}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SmallTestimonialCard({ testimonial, delay }: { testimonial: typeof TESTIMONIALS[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`bg-white rounded-lg p-7 border border-stone-200 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex gap-1 mb-4">
        {Array.from({ length: testimonial.rating }).map((_, i) => (
          <div key={i} className="w-4 h-4 flex items-center justify-center">
            <i className="ri-star-fill text-gold-400 text-sm" />
          </div>
        ))}
      </div>
      {/* Large quote mark */}
      <div className="font-serif text-stone-200 text-5xl leading-none -mb-2">"</div>
      <blockquote className="font-serif text-charcoal-800 text-base italic leading-relaxed mb-6">
        {testimonial.quote}
      </blockquote>
      <div className="pt-5 border-t border-stone-100">
        <div className="font-sans text-charcoal-900 text-sm font-semibold">{testimonial.name}</div>
        <div className="font-sans text-charcoal-700 text-xs mt-0.5">{testimonial.location} &bull; {testimonial.outcome}</div>
      </div>
    </div>
  );
}

function TestimonialCTA() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`bg-forest-500 rounded-lg p-7 flex flex-col justify-between transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div>
        <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-5">
          <i className="ri-star-fill text-gold-300 text-xl" />
        </div>
        <h3 className="font-serif text-white text-xl font-semibold mb-3">
          Join Our Happy Homeowners
        </h3>
        <p className="font-sans text-stone-200 text-sm leading-relaxed mb-6">
          Experience the difference premium design and craftsmanship makes. Your dream outdoor space is one consultation away.
        </p>
      </div>
      <a
        href="#consultation"
        onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
        className="inline-flex items-center gap-2 bg-white hover:bg-stone-100 text-forest-700 font-sans font-semibold text-sm px-6 py-3 rounded-md transition-all cursor-pointer whitespace-nowrap"
      >
        Request Consultation
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-line" />
        </div>
      </a>
    </div>
  );
}
